import React, { useCallback, useEffect, useState } from 'react';
import { ArrowRightCircleIcon, ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import AnimatedModal from '../ui/AnimatedModal';

/**
 * "Move to another job post" — Go Colors only.
 *
 * The dropdown is populated by the server, which also decides eligibility, so this component
 * never encodes the move-group rules itself. Targets already holding this person (matched on
 * email or phone) come back flagged `disabled` with a `duplicate`, and are rendered as
 * non-selectable so the clash is visible before the recruiter commits.
 *
 * On a duplicate the server answers 409; per the agreed behaviour the modal stays open, the
 * candidate is not moved, and the reason is shown inline in red.
 */
const MoveCandidateJobPostModal = ({
  isOpen,
  onClose,
  screeningId,
  candidate,
  onMoved,
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [targets, setTargets] = useState([]);
  const [sourceJobTitle, setSourceJobTitle] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');

  const candidateId = candidate?._id || candidate?.id || null;
  const candidateName = candidate?.name || 'This candidate';

  const resetState = useCallback(() => {
    setTargets([]);
    setSelectedId('');
    setError('');
    setLoadError('');
    setSourceJobTitle('');
  }, []);

  useEffect(() => {
    if (!isOpen || !screeningId || !candidateId) {
      return undefined;
    }
    let cancelled = false;
    resetState();
    setLoading(true);

    (async () => {
      try {
        const res = await axios.get(
          `/api/screenings/${screeningId}/candidates/${candidateId}/job-post-move-targets`
        );
        if (cancelled) return;
        const list = Array.isArray(res.data?.targets) ? res.data.targets : [];
        setTargets(list);
        setSourceJobTitle(res.data?.sourceJobTitle || '');
        // Preselect when there is exactly one usable target — the CSE ↔ Sr.CSE case, where
        // forcing a choice between one option is pure friction.
        const usable = list.filter((t) => !t.disabled);
        if (usable.length === 1) {
          setSelectedId(usable[0].jobPostingId);
        }
      } catch (e) {
        if (cancelled) return;
        setLoadError(
          e.response?.data?.message || 'Could not load the job posts this candidate can move to.'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, screeningId, candidateId, resetState]);

  const handleConfirm = async () => {
    if (!selectedId || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await axios.post(
        `/api/screenings/${screeningId}/candidates/${candidateId}/job-post-move`,
        { targetJobPostingId: selectedId }
      );
      if (typeof onMoved === 'function') {
        onMoved({ candidateId, ...res.data });
      }
      onClose();
    } catch (e) {
      // 409 = duplicate in the target job post. Keep the modal open and explain, per spec.
      setError(e.response?.data?.message || 'Could not move this candidate. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const selectableTargets = targets.filter((t) => !t.disabled);
  const hasNoTargets = !loading && !loadError && targets.length === 0;

  return (
    <AnimatedModal
      isOpen={isOpen}
      onClose={onClose}
      ariaLabelledBy="move-candidate-title"
      contentClassName="w-full max-w-lg rounded-xl bg-white shadow-xl"
    >
      <div className="p-6" data-testid="move-candidate-modal">
        <div className="flex items-start justify-between">
          <div>
            <h3 id="move-candidate-title" className="text-lg font-semibold text-gray-900">
              Move to another job post
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {candidateName}
              {sourceJobTitle ? ` — currently in ${sourceJobTitle}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-5">
          {loading && <p className="text-sm text-gray-500">Loading available job posts…</p>}

          {!loading && loadError && (
            <p className="text-sm text-red-600" data-testid="move-candidate-load-error">
              {loadError}
            </p>
          )}

          {hasNoTargets && (
            <p className="text-sm text-gray-600" data-testid="move-candidate-no-targets">
              There are no job posts this candidate can be moved to.
            </p>
          )}

          {!loading && !loadError && targets.length > 0 && (
            <>
              <label
                htmlFor="move-candidate-target"
                className="block text-sm font-medium text-gray-700"
              >
                Move to
              </label>
              <select
                id="move-candidate-target"
                data-testid="move-candidate-target-select"
                value={selectedId}
                onChange={(e) => {
                  setSelectedId(e.target.value);
                  setError('');
                }}
                className="mt-1.5 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#007BFF] focus:outline-none focus:ring-1 focus:ring-[#007BFF]"
              >
                <option value="">Select a job post…</option>
                {targets.map((t) => (
                  <option key={t.jobPostingId} value={t.jobPostingId} disabled={t.disabled}>
                    {t.title}
                    {t.disabled ? ' — already in this job post' : ''}
                  </option>
                ))}
              </select>

              {selectableTargets.length === 0 && (
                <p className="mt-2 text-sm text-amber-700" data-testid="move-candidate-all-disabled">
                  This candidate is already present in every job post they could move to.
                </p>
              )}

              <p className="mt-3 text-xs text-gray-500">
                Their resume, assessment, interview and documents move with them, into a
                <span className="font-medium text-gray-700"> Moved Candidates </span>
                screening on the destination job post. You can undo this from the candidate&apos;s
                row afterwards.
              </p>
            </>
          )}

          {error && (
            <div
              className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3"
              data-testid="move-candidate-error"
              role="alert"
            >
              <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!selectedId || submitting || loading}
            data-testid="move-candidate-confirm"
            className="inline-flex items-center gap-2 rounded-lg bg-[#007BFF] px-4 py-2 text-sm font-medium text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ArrowRightCircleIcon className="h-4 w-4" />
            {submitting ? 'Moving…' : 'Move candidate'}
          </button>
        </div>
      </div>
    </AnimatedModal>
  );
};

export default MoveCandidateJobPostModal;
